/**
 * Formatting helpers for the CVs list unit (Screen C/D/E).
 *
 * `formatRelativeTime` returns an i18n key + count for a timestamp, matching the
 * Figma "Nd ago" relative-time chip. The caller resolves the key via i18next so
 * the string stays localized (en/ar).
 */

export type RelativeTimeResult = {
  /** i18next key under `cv.list.relativeTime.*`. */
  key: 'now' | 'minutes' | 'hours' | 'days' | 'weeks' | 'months' | 'years';
  count: number;
};

const MINUTE = 60;
const HOUR = MINUTE * 60;
const DAY = HOUR * 24;
const WEEK = DAY * 7;
const MONTH = DAY * 30;
const YEAR = DAY * 365;

export function formatRelativeTime(
  dateStr: string,
  now: number = Date.now(),
): RelativeTimeResult {
  const then = new Date(dateStr).getTime();
  if (!Number.isFinite(then)) return { key: 'now', count: 0 };

  const seconds = Math.max(0, Math.floor((now - then) / 1000));

  if (seconds < MINUTE) return { key: 'now', count: 0 };
  if (seconds < HOUR) return { key: 'minutes', count: Math.floor(seconds / MINUTE) };
  if (seconds < DAY) return { key: 'hours', count: Math.floor(seconds / HOUR) };
  if (seconds < WEEK) return { key: 'days', count: Math.floor(seconds / DAY) };
  if (seconds < MONTH) return { key: 'weeks', count: Math.floor(seconds / WEEK) };
  if (seconds < YEAR) return { key: 'months', count: Math.floor(seconds / MONTH) };
  return { key: 'years', count: Math.floor(seconds / YEAR) };
}

/** Two-letter uppercase initials from a display name (e.g. "Hassan Al" -> "HA"). */
export function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * Avatar gradients for the "For others" owner cards. The first three pairs are
 * the exact Figma fills (node 3147-1554): purple, orange, green; the rest extend
 * the palette so distinct owners stay visually separable. Each pair is
 * `[start, end]` for a 90deg (start → end) horizontal gradient.
 */
const AVATAR_GRADIENTS: [string, string][] = [
  ['#8467FD', '#5A2AFF'], // purple
  ['#FDC167', '#FFAA2A'], // orange
  ['#3D9C70', '#128851'], // green
  ['#5AA9FF', '#2C7BE5'], // blue
  ['#E0719E', '#D9534F'], // rose
  ['#A06BE0', '#7A3FD0'], // violet
  ['#2DC6A6', '#16A085'], // teal
];

function hashKey(key: string): number {
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = (hash * 31 + key.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

/** Deterministic `[start, end]` avatar gradient from a stable key (id/email). */
export function avatarGradientFor(key: string): [string, string] {
  return AVATAR_GRADIENTS[hashKey(key) % AVATAR_GRADIENTS.length];
}

/** Deterministic flat avatar color (gradient end): fallback where gradients aren't used. */
export function avatarColorFor(key: string): string {
  return avatarGradientFor(key)[1];
}
